import React, { useState, useEffect, useRef } from 'react';
import { Star, Heart, MapPin, ThumbsUp } from 'lucide-react';
import { useTranslatedDesc } from '../hooks';
import { CAT_C } from '../constants';

// Scraper pollution guard: some managed_attractions rows have a `name` captured in
// the wrong locale (e.g. a Nagoya venue's `name` stored in Thai) while `name_en` is
// correct — Google Maps sometimes serves a business's own marketing-language listing
// name regardless of the scraper's browser locale. The site only has zh-Hant/zh-Hans/
// en/ja/ko display modes, so Thai script (or Korean hangul outside the Korean display
// mode) is never the right thing to show — fall back to the English name instead.
const WRONG_SCRIPT_RE = /[฀-๿가-힣]/; // Thai, Hangul
function guardScript(name, nameEn, lang) {
  if (!name || lang === 'ko') return name;
  return WRONG_SCRIPT_RE.test(name) ? (nameEn || name) : name;
}

export function localName(a, lang) {
  let name;
  if (lang === 'en')    name = a.nameEn    || a.name;
  else if (lang === 'zh-CN') name = a.nameZhCn  || a.name;
  else if (lang === 'ja')    name = a.nameJa    || a.nameEn || a.name;
  else if (lang === 'ko')    name = a.nameKo    || a.nameEn || a.name;
  else name = a.name;
  return guardScript(name, a.nameEn, lang);
}

const CAT_ICON = { culture:'🏛️', sight:'🌆', nature:'🌿', play:'🎡', shopping:'🛍️', food:'🍜' };

// Card thumbnails are ~150-300px wide; without a width param Unsplash/Pexels
// serve the full-size original (often >1MB each) to every card in a grid.
export function thumbUrl(url, w = 400) {
  if (!url) return url;
  try {
    const u = new URL(url);
    if (u.hostname === 'images.unsplash.com') {
      u.searchParams.set('w', String(w)); u.searchParams.set('q', '70'); u.searchParams.set('auto', 'format');
      return u.toString();
    }
    if (u.hostname === 'images.pexels.com') {
      u.searchParams.set('auto', 'compress'); u.searchParams.set('w', String(w));
      return u.toString();
    }
    if (u.hostname === 'commons.wikimedia.org' && u.pathname.includes('Special:FilePath')) {
      u.searchParams.set('width', String(w));
      return u.toString();
    }
    return url;
  } catch { return url; }
}

// Honest no-photo state: a category-tinted panel instead of a keyword-matched
// stock photo. A generic "temple in Hong Kong" image on a specific attraction
// card reads as THAT attraction's photo, which is worse than showing none.
export function CategoryPlaceholder({ category, dark = false }) {
  const cs = CAT_C[category] || CAT_C.all;
  return (
    <div style={{ width:'100%', height:'100%', display:'flex', flexDirection:'column',
      alignItems:'center', justifyContent:'center', gap:4,
      background: dark
        ? 'linear-gradient(135deg, #1e293b, #0f172a)'
        : `linear-gradient(135deg, ${cs.bg}, #ffffff)` }}>
      <span style={{ fontSize:32, lineHeight:1 }}>{CAT_ICON[category] || '📍'}</span>
    </div>
  );
}

export const AttractionCard = ({ a, idx, cityKey, t, lang, favorites, onToggleFav, onSelect, isLatest=false }) => {
  const cs = CAT_C[a.category]||CAT_C.all;
  const displayName = localName(a, lang);
  const isFav = favorites?.includes(a.name);
  const cardRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setIsVisible(true); obs.disconnect(); } },
      { rootMargin: '250px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => { setImgFailed(false); }, [a.imageUrl]);
  const showImg = a.imageUrl && !imgFailed;
  const displayDesc = useTranslatedDesc(a.desc, lang, isVisible);
  const [likes, setLikes] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ec_likes')||'{}')[a.name] || 0; } catch { return 0; }
  });
  const [liked, setLiked] = useState(() => {
    try { return (JSON.parse(localStorage.getItem('ec_liked')||'[]')).includes(a.name); } catch { return false; }
  });
  const handleLike = e => {
    e.stopPropagation();
    const next = !liked; const cnt = next ? likes+1 : Math.max(0, likes-1);
    setLiked(next); setLikes(cnt);
    try {
      const all = JSON.parse(localStorage.getItem('ec_likes')||'{}'); all[a.name]=cnt; localStorage.setItem('ec_likes', JSON.stringify(all));
      const arr = JSON.parse(localStorage.getItem('ec_liked')||'[]');
      next ? (!arr.includes(a.name)&&arr.push(a.name)) : (arr.splice(arr.indexOf(a.name),1));
      localStorage.setItem('ec_liked', JSON.stringify(arr));
    } catch {}
  };
  const handleNav = e => {
    e.stopPropagation();
    // place_id resolves to the actual place card (name, photos, reviews) —
    // a raw lat,lng search query just drops a pin labeled with the
    // coordinates, since Search API doesn't reverse-geocode it. Text search
    // is the last resort for older rows that predate place_id backfill.
    // query_place_id (not the legacy /maps/place/?q=place_id: form) — on
    // Android, Chrome hands google.com/maps links off to the Maps app via
    // App Links, and that handoff doesn't reliably resolve the legacy form,
    // sometimes landing on an unrelated place instead.
    const url = a.place_id
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(a.name)}&query_place_id=${a.place_id}`
      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((a.lat && a.lng) ? `${a.lat},${a.lng}` : `${a.name} ${cityKey}`)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  return (
    <div ref={cardRef} style={{ background:'white', borderRadius:16, overflow:'hidden', border:`1.5px solid ${isLatest?'#fde68a':'#e2e8f0'}`, cursor:'pointer', transition:'all 0.2s', position:'relative', flexShrink:0 }}
         onClick={()=>onSelect&&onSelect(a, cityKey)}
         onMouseEnter={e=>{e.currentTarget.style.boxShadow='0 8px 24px rgba(0,0,0,0.1)';e.currentTarget.style.transform='translateY(-2px)';}}
         onMouseLeave={e=>{e.currentTarget.style.boxShadow='none';e.currentTarget.style.transform='none';}}>
      <div style={{ position:'relative', height:125, overflow:'hidden', background:'#f1f5f9' }}>
        {showImg
          ? <img src={thumbUrl(a.imageUrl)} alt={a.name} loading="lazy"
              style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}
              onError={() => setImgFailed(true)}/>
          : <CategoryPlaceholder category={a.category}/>}
        {isLatest && (
          <span style={{ position:'absolute', top:7, left:7, background:'#dc2626', color:'white', fontSize:9, fontWeight:900, padding:'2px 7px', borderRadius:20, textTransform:'uppercase', letterSpacing:1 }}>NEW</span>
        )}
        {a.rating && (
          <div style={{ position:'absolute', top:7, right:36, background:'rgba(255,255,255,0.95)', borderRadius:20, padding:'2px 7px', display:'flex', alignItems:'center', gap:2 }}>
            <Star size={9} color="#f59e0b" fill="#f59e0b"/>
            <span style={{ fontSize:10, fontWeight:900, color:'#374151' }}>{Number(a.rating).toFixed(1)}</span>
          </div>
        )}
        <button aria-label={isFav?`移除${a.name}收藏`:`收藏${a.name}`} onClick={e=>{e.stopPropagation();onToggleFav&&onToggleFav(a.name, cityKey);}}
          style={{ position:'absolute', top:5, right:5, width:28, height:28, borderRadius:'50%', background:'rgba(255,255,255,0.92)', border:'none', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', boxShadow:'0 1px 6px rgba(0,0,0,0.15)', transition:'transform 0.15s', zIndex:2 }}
          onMouseEnter={e=>e.currentTarget.style.transform='scale(1.15)'}
          onMouseLeave={e=>e.currentTarget.style.transform='scale(1)'}>
          <Heart size={13} color={isFav?'#ef4444':'#94a3b8'} fill={isFav?'#ef4444':'none'}/>
        </button>
        <button onClick={handleNav}
          style={{ position:'absolute', bottom:6, right:5, width:26, height:26, borderRadius:'50%', background:'rgba(255,255,255,0.92)', border:'none', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', boxShadow:'0 1px 6px rgba(0,0,0,0.15)', zIndex:2 }}
          title="在 Google Maps 開啟">
          <MapPin size={12} color="#1677ff"/>
        </button>
      </div>
      <div style={{ padding:'8px 10px 10px' }}>
        <p style={{ fontSize:11, fontWeight:900, color:'#0f172a', margin:'0 0 3px', lineHeight:1.3 }}>{displayName}</p>
        <p style={{ fontSize:10, color:'#64748b', margin:'0 0 7px', lineHeight:1.4, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>{displayDesc}</p>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <span style={{ fontSize:9, fontWeight:900, textTransform:'uppercase', letterSpacing:1, background:cs.bg, color:cs.text, padding:'2px 7px', borderRadius:6 }}>{t[a.category]||a.category}</span>
          <button onClick={handleLike}
            style={{ display:'flex', alignItems:'center', gap:4, background:'none', border:'none', cursor:'pointer', padding:'2px 4px', borderRadius:6, transition:'all 0.15s',
              color: liked ? '#1677ff' : '#94a3b8' }}
            onMouseEnter={e=>e.currentTarget.style.background='#f0f9ff'}
            onMouseLeave={e=>e.currentTarget.style.background='none'}>
            <ThumbsUp size={11} fill={liked?'#1677ff':'none'} color={liked?'#1677ff':'#94a3b8'}/>
            {likes > 0 && <span style={{ fontSize:9, fontWeight:900 }}>{likes}</span>}
          </button>
        </div>
      </div>
    </div>
  );
};

export const LatestCard = ({ a, i, cityKey, t, lang, favorites, onToggleFav, onSelect }) => {
  const cs = CAT_C[a.category]||CAT_C.all;
  const displayName = localName(a, lang);
  const isFav = favorites?.includes(a.name);
  const cardRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setIsVisible(true); obs.disconnect(); } },
      { rootMargin: '250px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => { setImgFailed(false); }, [a.imageUrl]);
  const showImg = a.imageUrl && !imgFailed;
  const displayDesc = useTranslatedDesc(a.desc, lang, isVisible);
  const [likes, setLikes] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ec_likes')||'{}')[a.name] || 0; } catch { return 0; }
  });
  const [liked, setLiked] = useState(() => {
    try { return (JSON.parse(localStorage.getItem('ec_liked')||'[]')).includes(a.name); } catch { return false; }
  });
  const handleLike = e => {
    e.stopPropagation();
    const next = !liked; const cnt = next ? likes+1 : Math.max(0, likes-1);
    setLiked(next); setLikes(cnt);
    try {
      const all = JSON.parse(localStorage.getItem('ec_likes')||'{}'); all[a.name]=cnt; localStorage.setItem('ec_likes', JSON.stringify(all));
      const arr = JSON.parse(localStorage.getItem('ec_liked')||'[]');
      next ? (!arr.includes(a.name)&&arr.push(a.name)) : (arr.splice(arr.indexOf(a.name),1));
      localStorage.setItem('ec_liked', JSON.stringify(arr));
    } catch {}
  };
  const handleNav = e => {
    e.stopPropagation();
    // place_id resolves to the actual place card (name, photos, reviews) —
    // a raw lat,lng search query just drops a pin labeled with the
    // coordinates, since Search API doesn't reverse-geocode it. Text search
    // is the last resort for older rows that predate place_id backfill.
    // query_place_id (not the legacy /maps/place/?q=place_id: form) — on
    // Android, Chrome hands google.com/maps links off to the Maps app via
    // App Links, and that handoff doesn't reliably resolve the legacy form,
    // sometimes landing on an unrelated place instead.
    const url = a.place_id
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(a.name)}&query_place_id=${a.place_id}`
      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((a.lat && a.lng) ? `${a.lat},${a.lng}` : `${a.name} ${cityKey}`)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  return (
    <div ref={cardRef} style={{ background:'white', borderRadius:18, overflow:'hidden', border:'2px solid #fde68a', position:'relative', transition:'all 0.2s', cursor:'pointer' }}
         onClick={()=>onSelect&&onSelect(a, cityKey)}
         onMouseEnter={e=>{e.currentTarget.style.boxShadow='0 8px 28px rgba(239,68,68,0.15)';e.currentTarget.style.transform='translateY(-2px)';}}
         onMouseLeave={e=>{e.currentTarget.style.boxShadow='none';e.currentTarget.style.transform='none';}}>
      <div style={{ position:'relative', height:125, overflow:'hidden', background:'#f1f5f9' }}>
        {showImg
          ? <img src={thumbUrl(a.imageUrl)} alt={a.name} loading="lazy"
              style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}
              onError={() => setImgFailed(true)}/>
          : <CategoryPlaceholder category={a.category}/>}
        <div style={{ position:'absolute', top:0, left:0, background:'linear-gradient(135deg,#dc2626,#ea580c)', color:'white', fontSize:9, fontWeight:900, padding:'4px 12px 4px 8px', borderBottomRightRadius:12, letterSpacing:1.5, textTransform:'uppercase' }}>
          ✦ NEW
        </div>
        {a.openedWhen && (
          <div style={{ position:'absolute', bottom:7, left:8, background:'rgba(0,0,0,0.7)', backdropFilter:'blur(4px)', color:'white', fontSize:9, fontWeight:700, padding:'3px 8px', borderRadius:20 }}>
            📅 {a.openedWhen}
          </div>
        )}
        <button aria-label={isFav?`移除${a.name}收藏`:`收藏${a.name}`} onClick={e=>{e.stopPropagation();onToggleFav&&onToggleFav(a.name, cityKey);}}
          style={{ position:'absolute', top:7, right:7, width:28, height:28, borderRadius:'50%', background:'rgba(255,255,255,0.92)', border:'none', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', boxShadow:'0 1px 6px rgba(0,0,0,0.18)', zIndex:2 }}>
          <Heart size={13} color={isFav?'#ef4444':'#94a3b8'} fill={isFav?'#ef4444':'none'}/>
        </button>
        <button aria-label="在 Google Maps 開啟" onClick={handleNav}
          style={{ position:'absolute', bottom:7, right:7, width:26, height:26, borderRadius:'50%', background:'rgba(255,255,255,0.92)', border:'none', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', boxShadow:'0 1px 6px rgba(0,0,0,0.18)', zIndex:2 }}
          title="在 Google Maps 開啟">
          <MapPin size={12} color="#1677ff"/>
        </button>
      </div>
      <div style={{ padding:'10px 12px 12px' }}>
        <p style={{ fontSize:12, fontWeight:900, color:'#0f172a', margin:'0 0 4px', lineHeight:1.3 }}>{displayName}</p>
        <p style={{ fontSize:10, color:'#64748b', margin:'0 0 8px', lineHeight:1.45, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>{displayDesc}</p>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <span style={{ fontSize:9, fontWeight:900, textTransform:'uppercase', letterSpacing:1, background:cs.bg, color:cs.text, padding:'2px 8px', borderRadius:6 }}>{t[a.category]||a.category}</span>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {a.rating && <div style={{ display:'flex', alignItems:'center', gap:2 }}><Star size={9} color="#f59e0b" fill="#f59e0b"/><span style={{ fontSize:10, fontWeight:900, color:'#374151' }}>{Number(a.rating).toFixed(1)}</span></div>}
            <button onClick={handleLike}
              style={{ display:'flex', alignItems:'center', gap:3, background:'none', border:'none', cursor:'pointer', padding:'2px 4px', borderRadius:6, color: liked ? '#1677ff' : '#94a3b8', transition:'all 0.15s' }}
              onMouseEnter={e=>e.currentTarget.style.background='#f0f9ff'}
              onMouseLeave={e=>e.currentTarget.style.background='none'}>
              <ThumbsUp size={11} fill={liked?'#1677ff':'none'} color={liked?'#1677ff':'#94a3b8'}/>
              {likes > 0 && <span style={{ fontSize:9, fontWeight:900 }}>{likes}</span>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
